import React, { useEffect, useMemo, useState } from 'react';
import { Card } from './Card';
import { Icon } from './icons';
import { Form } from './Form';
import { Tiles } from './Tiles';
import { chicoryFetcher, debounce, isApiError } from '../utils';
import {
  AUTH0_FAILED_MESSAGE,
  Auth0Failure,
  addPartnerErrorMessage,
  auth0FailedAgainMessage,
  auth0FailureFromError,
  publisherNotFoundMessage,
} from '../utils/addPartnerMessages';
import { Account, Publisher } from '../types';

import './AdminAddNewUserForm.pcss';
import { useDispatch } from 'react-redux';
import { AppDispatch } from '../store';
import { fetchCurrentPartners } from '../store/admin/actions';

const FOUND_ACCOUNT = `
  This publisher belongs to the account below.
  The new partner will see the data of every publisher in this account.
`;

type Lookup =
  | { state: 'idle' }
  | { state: 'notFound' }
  | { state: 'failed' }
  | { state: 'hasAccount'; account: Account }
  | { state: 'noAccount'; publisher: Publisher };

const NewUserForm: React.FC<{ onClose: () => void }> = ({ onClose }) => {
  const receivePubIdInput = useMemo(() => {
    return debounce((userInput: string) => {
      setPubId(userInput.trim() || null);
    }, 500);
  }, []);

  const dispatch = useDispatch<AppDispatch>();

  const [typedPubId, setPubId] = useState<string | null>(null);
  const [lookup, setLookup] = useState<Lookup>({ state: 'idle' });
  // "Add another partner" runs the lookup again, because the submit may have created the account.
  const [lookupCount, setLookupCount] = useState(0);

  const [accountName, setAccountName] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');

  const [submitting, setSubmitting] = useState(false);
  const [submissionError, setSubmissionError] = useState<string | null>(null);
  // Set once the partner row exists. The form is hidden then, because a resubmit would get 409
  // for the email.
  const [addedMessage, setAddedMessage] = useState<string | null>(null);
  const [auth0Failure, setAuth0Failure] = useState<
    (Auth0Failure & { retried: boolean }) | null
  >(null);
  const [retryingAuth0, setRetryingAuth0] = useState(false);

  useEffect(() => {
    setLookup({ state: 'idle' });
    setAddedMessage(null);
    setAuth0Failure(null);
    setSubmissionError(null);
    if (typedPubId === null) return;

    // A slow response for an earlier publisher ID must not replace the result for the current ID.
    let current = true;
    const findAccount = async () => {
      const publisher: Publisher = await (
        await chicoryFetcher(`/publishers/${typedPubId}`)
      ).json();
      if (!publisher.accountId) {
        return { state: 'noAccount', publisher } as const;
      }
      const account: Account = await (
        await chicoryFetcher(`/accounts/${publisher.accountId}`)
      ).json();
      return { state: 'hasAccount', account } as const;
    };

    findAccount()
      .then((result) => {
        if (!current) return;
        if (result.state === 'noAccount') {
          setAccountName(result.publisher.hostname || result.publisher.name);
        }
        setLookup(result);
      })
      .catch((e) => {
        if (!current) return;
        setLookup(
          isApiError(e) && e.status === 404
            ? { state: 'notFound' }
            : { state: 'failed' }
        );
      });

    return () => {
      current = false;
    };
  }, [typedPubId, lookupCount]);

  const onSubmission = async () => {
    if (lookup.state !== 'hasAccount' && lookup.state !== 'noAccount') return;
    setSubmissionError(null);

    const partner = {
      firstName: firstName.trim(),
      lastName: lastName.trim(),
      email: email.trim(),
    };
    const newAccountName = accountName.trim();
    if (
      !partner.firstName ||
      !partner.lastName ||
      !partner.email ||
      (lookup.state === 'noAccount' && !newAccountName)
    ) {
      setSubmissionError('Fill in every field.');
      return;
    }

    const accountFields =
      lookup.state === 'hasAccount'
        ? { accountId: lookup.account.id }
        : {
            accountName: newAccountName,
            publishers: [
              {
                id: lookup.publisher.id,
                name: lookup.publisher.name,
                hostname: lookup.publisher.hostname,
              },
            ],
          };

    // The partner row exists after a success and after an Auth0 failure.
    const partnerCreated = () => {
      setFirstName('');
      setLastName('');
      setEmail('');
      dispatch(fetchCurrentPartners());
      const who = `${partner.firstName} ${partner.lastName} (${partner.email})`;
      setAddedMessage(
        lookup.state === 'noAccount'
          ? `Created the account "${newAccountName}" and added ${who}.`
          : `Added ${who} to the account "${lookup.account.name}".`
      );
    };

    setSubmitting(true);
    setAuth0Failure(null);
    try {
      await chicoryFetcher(`/partners`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          ...partner,
          status: 'approved',
          ...accountFields,
        }),
      });
      partnerCreated();
    } catch (e) {
      const failure = isApiError(e)
        ? auth0FailureFromError(e.status, e.body)
        : null;
      if (failure) {
        partnerCreated();
        setAuth0Failure({ ...failure, retried: false });
      } else {
        setSubmissionError(
          isApiError(e)
            ? addPartnerErrorMessage(e.status, e.body, partner, newAccountName)
            : addPartnerErrorMessage(0, null, partner)
        );
      }
    }
    setSubmitting(false);
  };

  const addAnotherPartner = () => {
    setLookupCount((count) => count + 1);
  };

  const retryAuth0Login = async () => {
    if (!auth0Failure) return;
    setRetryingAuth0(true);
    try {
      await chicoryFetcher(`/partners/${auth0Failure.partnerId}/auth0-login`, {
        method: 'POST',
      });
      setAuth0Failure(null);
    } catch (e) {
      const failure = isApiError(e)
        ? auth0FailureFromError(e.status, e.body)
        : null;
      setAuth0Failure({ ...(failure ?? auth0Failure), retried: true });
    }
    setRetryingAuth0(false);
  };

  const showPartnerFields =
    !addedMessage &&
    (lookup.state === 'hasAccount' || lookup.state === 'noAccount');

  return (
    <Card id="admin-new-user-form">
      <Card.Header>
        <Card.HeaderLeft>
          <Icon className="add-user-icon" name="user-01" />
          <Card.TitleSmall>Add a partner</Card.TitleSmall>
        </Card.HeaderLeft>
        <Card.HeaderRight>
          <Icon name="x" className="close-icon" onClick={onClose} />
        </Card.HeaderRight>
      </Card.Header>
      <Card.Divider />
      <Card.Body>
        <Form.TitleSmall style={{ marginBottom: '0.5rem' }}>
          Publisher ID of the partner:
        </Form.TitleSmall>
        <Form.InputContainer>
          <input
            type="text"
            onChange={(e) => {
              receivePubIdInput(e.target.value);
            }}
          />
        </Form.InputContainer>
        <div>
          {lookup.state === 'notFound' && typedPubId && (
            <Form.TitleSmall>
              {publisherNotFoundMessage(typedPubId)}
            </Form.TitleSmall>
          )}
          {lookup.state === 'failed' && (
            <Form.TitleSmall>
              The publisher could not be loaded. Try again. If the problem
              continues, ask engineering.
            </Form.TitleSmall>
          )}
          {!addedMessage && lookup.state === 'hasAccount' && (
            <div className="lookup-message">
              <Form.TitleSmall>{FOUND_ACCOUNT}</Form.TitleSmall>
              <div className="account-information">
                <h4>{lookup.account.name}:</h4>
                <span>
                  {lookup.account.publishers
                    .map((pub) => pub.hostname)
                    .join(', ')}
                </span>
              </div>
            </div>
          )}
          {!addedMessage && lookup.state === 'noAccount' && (
            <div className="lookup-message">
              <Form.TitleSmall>
                {lookup.publisher.hostname || lookup.publisher.name} has no
                account yet. Submitting creates a new account with this
                publisher.
              </Form.TitleSmall>
            </div>
          )}
          {submissionError && (
            <div className="submission-message-container">
              <span className="red">{submissionError}</span>
            </div>
          )}
          {addedMessage && !auth0Failure && (
            <div className="submission-message-container">
              <span className="green">{addedMessage}</span>
            </div>
          )}
          {auth0Failure && (
            <div className="submission-message-container auth0-retry">
              <span className="red">
                {auth0Failure.retried
                  ? auth0FailedAgainMessage(auth0Failure)
                  : AUTH0_FAILED_MESSAGE}
              </span>
              <Tiles.Button
                version="darkPurple"
                style={{ fontSize: '16px', padding: '0.5rem 1rem' }}
                onClick={!retryingAuth0 ? retryAuth0Login : () => {}}
              >
                {retryingAuth0 ? '...' : 'Try again'}
              </Tiles.Button>
            </div>
          )}
          {addedMessage && (
            <div className="buttons-container">
              <Tiles.Button
                version="white"
                style={{ fontSize: '16px', width: '50%', padding: '1rem' }}
                onClick={onClose}
              >
                Close
              </Tiles.Button>
              <Tiles.Button
                version="darkPurple"
                style={{ fontSize: '16px', width: '50%', padding: '1rem' }}
                onClick={addAnotherPartner}
              >
                Add another partner
              </Tiles.Button>
            </div>
          )}
          {showPartnerFields && (
            <form id="admin-new-user-form-partner-fields">
              {lookup.state === 'noAccount' && (
                <div className="field-section">
                  <Form.Label>
                    <span>Account name</span>
                    <span>Partners see this name</span>
                  </Form.Label>
                  <Form.InputContainer>
                    <input
                      type="text"
                      onChange={(e) => setAccountName(e.target.value)}
                      value={accountName}
                    />
                  </Form.InputContainer>
                </div>
              )}
              <div className="field-section">
                <Form.Label>
                  <span>Partner first name</span>
                </Form.Label>
                <Form.InputContainer>
                  <input
                    type="text"
                    onChange={(e) => setFirstName(e.target.value)}
                    value={firstName}
                  />
                </Form.InputContainer>
              </div>
              <div className="field-section">
                <Form.Label>
                  <span>Partner last name</span>
                </Form.Label>
                <Form.InputContainer>
                  <input
                    type="text"
                    onChange={(e) => setLastName(e.target.value)}
                    value={lastName}
                  />
                </Form.InputContainer>
              </div>
              <div className="field-section">
                <Form.Label>
                  <span>Partner email</span>
                  <span>Used for the Auth0 login</span>
                </Form.Label>
                <Form.InputContainer>
                  <input
                    type="email"
                    onChange={(e) => setEmail(e.target.value)}
                    value={email}
                  />
                </Form.InputContainer>
              </div>
              <div className="buttons-container">
                <Tiles.Button
                  version="white"
                  style={{
                    fontSize: '16px',
                    width: '50%',
                    padding: '1rem',
                  }}
                  onClick={onClose}
                >
                  Cancel
                </Tiles.Button>
                <Tiles.Button
                  version="darkPurple"
                  style={{
                    fontSize: '16px',
                    width: '50%',
                    padding: '1rem',
                  }}
                  onClick={!submitting ? onSubmission : () => {}}
                >
                  {submitting
                    ? '...'
                    : lookup.state === 'noAccount'
                      ? 'Create account and partner'
                      : 'Add partner'}
                </Tiles.Button>
              </div>
            </form>
          )}
        </div>
      </Card.Body>
    </Card>
  );
};

export default NewUserForm;
